export const getCodeFenceLanguage = (
  code: string | null | undefined,
  defaultLanguage = ""
): string => {
  const match = (code ?? "").match(/^```([a-zA-Z]+)/);
  return match ? match[1].toLowerCase() : defaultLanguage;
};

export const stripCodeFence = (code: string | null | undefined): string =>
  (code ?? "")
    .replace(/^```[a-zA-Z]*\n?/, "")
    .replace(/\n?```$/, "")
    .trim();

/**
 * Splits a leading markdown fence off a string, returning the fence (markers
 * included, as `CodeBlock` accepts) and the prose that follows it.
 *
 * Older storyboards packed a code snippet into a text field this way instead of
 * carrying it in a dedicated one. Returns null when there is no leading fence.
 */
export const splitLeadingFence = (
  text: string
): { fence: string; rest: string } | null => {
  const match = text.match(/^\s*(```[a-zA-Z]*\n[\s\S]*?```)\s*([\s\S]*)$/);
  return match ? { fence: match[1], rest: match[2].trim() } : null;
};

/**
 * Splits markdown so a prompt can sit in QuizEntryCounter and fenced code
 * renders below it (CODE_MULTIPLE_CHOICE, matching CODE_TRUE_FALSE).
 *
 * A leading fence is reordered like the true/false legacy shape (prose first,
 * fence below). Otherwise the split is at the first fence so a trailing
 * snippet stays below the question. Remainder is null when there is no fence.
 */
export const splitPromptAndFences = (
  text: string
): { prompt: string; remainder: string | null } => {
  const leading = splitLeadingFence(text);
  if (leading) {
    return {
      prompt: leading.rest,
      remainder: leading.fence,
    };
  }

  const trailing = text.match(/^([\s\S]*?)(```[a-zA-Z]*\n[\s\S]*)$/);
  if (!trailing) return { prompt: text.trim(), remainder: null };

  return { prompt: trailing[1].trim(), remainder: trailing[2].trim() };
};

const BRACE_LANGUAGES = new Set([
  "java",
  "javascript",
  "js",
  "jsx",
  "typescript",
  "ts",
  "tsx",
  "c",
  "cpp",
  "csharp",
  "cs",
  "kotlin",
  "scala",
  "go",
  "rust",
  "php",
  "swift",
  "dart",
  "groovy",
]);

/**
 * Reflows code that has been flattened onto a single line by an LLM.
 *
 * This is intentionally not a parser. It only inserts line breaks and
 * indentation around braces and statement terminators while respecting
 * strings and block comments.
 */
export const reflowFlattenedCode = (
  code: string,
  language?: string,
  indent = "    "
): string => {
  if (language && !BRACE_LANGUAGES.has(language.toLowerCase())) {
    return code;
  }

  // Already formatted? Leave it alone.
  const nonEmptyLines = code.split("\n").filter((l) => l.trim()).length;
  if (nonEmptyLines > 1) {
    return code;
  }

  const markerCount = (code.match(/[;{}]/g) ?? []).length;
  if (markerCount < 2) {
    return code;
  }

  // No brace-balance check here: real content splits classes across
  // sections, so fragments with unbalanced braces are the primary input.
  const lines: string[] = [];

  let buf = "";
  let depth = 0;
  let parenDepth = 0;

  let stringDelimiter = "";
  let inBlockComment = false;

  const pushLine = () => {
    const trimmed = buf.trim();
    if (trimmed) {
      lines.push(indent.repeat(depth) + trimmed);
    }
    buf = "";
  };

  for (let i = 0; i < code.length; i++) {
    const ch = code[i];

    //
    // Inside string
    //
    if (stringDelimiter) {
      buf += ch;

      if (ch === "\\") {
        buf += code[++i] ?? "";
      } else if (ch === stringDelimiter) {
        stringDelimiter = "";
      }

      continue;
    }

    //
    // Inside block comment
    //
    if (inBlockComment) {
      buf += ch;

      if (ch === "*" && code[i + 1] === "/") {
        buf += code[++i];
        inBlockComment = false;
        pushLine();
      }

      continue;
    }

    //
    // Start of string
    //
    if (ch === '"' || ch === "'") {
      stringDelimiter = ch;
      buf += ch;
      continue;
    }

    //
    // Block comment
    //
    if (ch === "/" && code[i + 1] === "*") {
      pushLine();
      buf = "/*";
      inBlockComment = true;
      i++;
      continue;
    }

    //
    // Line comment
    //
    if (ch === "/" && code[i + 1] === "/") {
      pushLine();
      buf = code.slice(i);
      pushLine();
      break;
    }

    //
    // Parentheses
    //
    if (ch === "(") {
      parenDepth++;
      buf += ch;
      continue;
    }

    if (ch === ")") {
      parenDepth = Math.max(0, parenDepth - 1);
      buf += ch;
      continue;
    }

    //
    // Statement terminator
    //
    if (ch === ";" && parenDepth === 0) {
      buf += ch;
      pushLine();
      continue;
    }

    //
    // Opening brace
    //
    if (ch === "{") {
      buf += "{";
      pushLine();
      depth++;
      continue;
    }

    //
    // Closing brace
    //
    if (ch === "}") {
      pushLine();

      depth = Math.max(0, depth - 1);

      buf = "}";

      let next = i + 1;

      while (code[next] === " ") {
        next++;
      }

      // Keep }, and }; together
      if (code[next] === ";" || code[next] === ",") {
        buf += code[next];
        i = next;
      }

      pushLine();
      continue;
    }

    //
    // Default
    //
    buf += ch;
  }

  pushLine();

  return lines.join("\n");
};

/**
 * Scans an array of raw code strings (may include code fence tags) and returns
 * the most frequently occurring explicit language tag. Returns "" when none are found.
 */
export const deriveDefaultLanguage = (
  codeStrings: (string | null | undefined)[]
): string => {
  const counts: Record<string, number> = {};
  for (const code of codeStrings) {
    const match = (code ?? "").match(/^```([a-zA-Z]+)/);
    if (match) {
      const lang = match[1].toLowerCase();
      counts[lang] = (counts[lang] ?? 0) + 1;
    }
  }
  const entries = Object.entries(counts);
  if (entries.length === 0) return "";
  return entries.reduce((a, b) => (b[1] > a[1] ? b : a))[0];
};
